import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { getElevenLabsVoices } from '../../services/api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const EMPTY_VOICE = {
  provider: 'elevenlabs',
  providerVoiceId: '',
  name: '',
  internalDescription: '',
  clientDescription: '',
  sampleUrl: '',
  previewText: '',
  tags: { gender: 'unspecified', accent: '', style: [], ageRange: 'unspecified' },
  isActive: true
};

const EMPTY_AVATAR = {
  name: '',
  style: 'illustrated',
  description: '',
  clientDescription: '',
  imageUrl: '',
  isActive: true
};

const STYLE_OPTIONS = ['abstract', 'illustrated', 'semi_realistic', 'photo_real'];
const GENDER_OPTIONS = ['female', 'male', 'neutral', 'unspecified'];
const AGE_OPTIONS = ['teen', 'young_adult', 'adult', 'mature', 'unspecified'];

const authHeaders = token => ({ headers: { 'x-auth-token': token } });

// ElevenLabs labels.age values are free-form ("young", "middle_aged", etc).
// Map to our constrained enum. Unknown → unspecified.
function mapElevenLabsAge(age) {
  const v = String(age || '').toLowerCase();
  if (v.includes('teen')) return 'teen';
  if (v.includes('young')) return 'young_adult';
  if (v.includes('middle')) return 'adult';
  if (v.includes('old') || v.includes('mature') || v.includes('senior')) return 'mature';
  return 'unspecified';
}

const VoiceAndAvatarManager = ({ token }) => {
  const [tab, setTab] = useState(0);
  const [voices, setVoices] = useState([]);
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [voiceEditor, setVoiceEditor] = useState(null); // voice object or null
  const [equivalentsEditor, setEquivalentsEditor] = useState(null); // voice object or null
  const [savingEquivalents, setSavingEquivalents] = useState(false);
  const [avatarEditor, setAvatarEditor] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [testAudio, setTestAudio] = useState(null);
  // ElevenLabs library browser — source the real voice pool from the
  // workspace's ElevenLabs account rather than asking the admin to type
  // provider IDs by hand.
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryVoices, setLibraryVoices] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState(null);
  const [libraryPreviewing, setLibraryPreviewing] = useState(null);

  const loadVoices = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API_BASE}/admin/voice-library/voices?includeInactive=1`, authHeaders(token));
      setVoices(res.data.voices || []);
    } catch (e) { setError(e.response?.data?.msg || e.message); }
    setLoading(false);
  }, [token]);

  const loadAvatars = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API_BASE}/admin/voice-library/avatars?includeInactive=1`, authHeaders(token));
      setAvatars(res.data.avatars || []);
    } catch (e) { setError(e.response?.data?.msg || e.message); }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    if (tab === 0) loadVoices(); else loadAvatars();
  }, [tab, loadVoices, loadAvatars]);

  // ---------------- ElevenLabs library browser ----------------
  const openLibrary = async () => {
    setLibraryOpen(true);
    setLibraryError(null);
    if (libraryVoices.length > 0) return; // cached after first fetch
    setLibraryLoading(true);
    try {
      const voices = await getElevenLabsVoices();
      setLibraryVoices(voices || []);
    } catch (e) {
      setLibraryError(e.message || 'Failed to load ElevenLabs voices');
    }
    setLibraryLoading(false);
  };

  const isAlreadyInCatalog = providerVoiceId =>
    voices.some(v => v.providerVoiceId === providerVoiceId);

  // Pull an ElevenLabs library voice into our editor. Pre-fills what
  // ElevenLabs gives us; admin still owns the display name + descriptions.
  const importFromLibrary = libraryVoice => {
    const labels = libraryVoice.labels || {};
    setLibraryOpen(false);
    setVoiceEditor({
      provider: 'elevenlabs',
      providerVoiceId: libraryVoice.voice_id,
      name: libraryVoice.name || '',
      internalDescription: labels.description || labels.use_case || '',
      clientDescription: '',
      // Previews now synthesize controlled text; don't carry the provider's
      // default sample clip into the catalog.
      sampleUrl: '',
      previewText: '',
      tags: {
        gender: ['female', 'male', 'neutral'].includes(labels.gender) ? labels.gender : 'unspecified',
        accent: labels.accent || '',
        style: labels.description ? [labels.description] : [],
        ageRange: mapElevenLabsAge(labels.age)
      },
      isActive: true
    });
  };

  const previewLibraryVoice = async libraryVoice => {
    setLibraryPreviewing(libraryVoice.voice_id);
    try {
      // Always synthesize controlled text via the backend (keeps the ElevenLabs
      // key server-side and previews our content, not the provider default sample).
      const res = await axios.post(
        `${API_BASE}/tts/generate`,
        { text: `Hi — this is ${libraryVoice.name}.`, voiceId: libraryVoice.voice_id },
        authHeaders(token)
      );
      if (res.data?.audio) {
        const audio = new Audio(`data:audio/mp3;base64,${res.data.audio}`);
        audio.onended = () => setLibraryPreviewing(null);
        audio.play();
      } else {
        setLibraryPreviewing(null);
      }
    } catch (e) {
      setLibraryError(`Preview failed: ${e.message}`);
      setLibraryPreviewing(null);
    }
  };

  // ---------------- Voice helpers ----------------
  const saveVoice = async () => {
    const payload = { ...voiceEditor };
    try {
      if (payload._id) {
        await axios.put(`${API_BASE}/admin/voice-library/voices/${payload._id}`, payload, authHeaders(token));
      } else {
        await axios.post(`${API_BASE}/admin/voice-library/voices`, payload, authHeaders(token));
      }
      setVoiceEditor(null);
      loadVoices();
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    }
  };

  const deleteVoice = async id => {
    if (!window.confirm('Retire this voice? Existing references will keep working but the voice will be hidden from new pickers.')) return;
    try {
      await axios.delete(`${API_BASE}/admin/voice-library/voices/${id}`, authHeaders(token));
      loadVoices();
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    }
  };

  // Save the cross-provider equivalents block for the voice currently in the
  // equivalents editor. Only the equivalents field is sent — other voice
  // metadata is unaffected. Server invalidates resolver cache so the new
  // mapping takes effect immediately.
  const saveEquivalents = async () => {
    if (!equivalentsEditor?._id) return;
    setSavingEquivalents(true);
    try {
      const equivalents = {};
      for (const provider of ['elevenlabs', 'google', 'azure', 'openai']) {
        const e = equivalentsEditor.equivalents?.[provider];
        if (e && (e.providerVoiceId || e.qualityNote)) {
          equivalents[provider] = {
            providerVoiceId: e.providerVoiceId?.trim() || null,
            voiceCatalogId: e.voiceCatalogId || null,
            qualityNote: (e.qualityNote || '').trim()
          };
        }
      }
      await axios.put(
        `${API_BASE}/admin/voice-library/voices/${equivalentsEditor._id}/equivalents`,
        { equivalents },
        authHeaders(token)
      );
      setEquivalentsEditor(null);
      loadVoices();
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    } finally {
      setSavingEquivalents(false);
    }
  };

  const testVoice = async voice => {
    setTestAudio(null);
    try {
      // Preview the actual configured text where set, so admins hear what
      // clients will hear; fall back to a generic line otherwise.
      const text = (voice.previewText && voice.previewText.trim())
        ? voice.previewText
        : `Hi — this is ${voice.name}. ${voice.clientDescription || ''}`;
      const res = await axios.post(
        `${API_BASE}/tts/generate`,
        { text, voiceId: voice.providerVoiceId },
        authHeaders(token)
      );
      if (res.data?.audio) {
        const audio = new Audio(`data:audio/mp3;base64,${res.data.audio}`);
        audio.play();
        setTestAudio(voice._id);
      }
    } catch (e) {
      setError(`Test failed: ${e.response?.data?.msg || e.message}`);
    }
  };

  // ---------------- Avatar helpers ----------------
  const saveAvatar = async () => {
    try {
      const isUpdate = Boolean(avatarEditor._id);
      const url = isUpdate
        ? `${API_BASE}/admin/voice-library/avatars/${avatarEditor._id}`
        : `${API_BASE}/admin/voice-library/avatars`;

      let payload = { ...avatarEditor };
      let config = authHeaders(token);

      if (avatarFile) {
        const fd = new FormData();
        Object.entries(payload).forEach(([k, v]) => {
          if (v !== undefined && v !== null && k !== 'imageUrl') fd.append(k, String(v));
        });
        fd.append('image', avatarFile);
        payload = fd;
        config = { headers: { 'x-auth-token': token, 'Content-Type': 'multipart/form-data' } };
      }

      if (isUpdate) await axios.put(url, payload, config);
      else await axios.post(url, payload, config);

      setAvatarEditor(null);
      setAvatarFile(null);
      loadAvatars();
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    }
  };

  const deleteAvatar = async id => {
    if (!window.confirm('Retire this avatar? Personas using it will keep the denormalized image URL until reassigned.')) return;
    try {
      await axios.delete(`${API_BASE}/admin/voice-library/avatars/${id}`, authHeaders(token));
      loadAvatars();
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    }
  };

  return (
    <Paper sx={{ p: 3, m: 2 }}>
      <Typography variant="h5" gutterBottom>Voice &amp; Avatar Library</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Admin-managed pool. Therapists whitelist a subset for their clients; clients pick from the therapist whitelist (or the platform default pool when no therapist is assigned).
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Voices (${voices.length})`} />
        <Tab label={`Avatars (${avatars.length})`} />
      </Tabs>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}

      {/* -------------------- Voices tab -------------------- */}
      {tab === 0 && !loading && (
        <Box>
          <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
            <Button startIcon={<LibraryMusicIcon />} variant="contained" onClick={openLibrary}>
              Import from ElevenLabs
            </Button>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setVoiceEditor({ ...EMPTY_VOICE })}>
              Add Manually
            </Button>
          </Box>
          <Grid container spacing={2}>
            {voices.map(v => (
              <Grid item xs={12} md={6} lg={4} key={v._id}>
                <Paper variant="outlined" sx={{ p: 2, opacity: v.isActive === false ? 0.55 : 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>{v.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {v.provider} • {v.providerVoiceId}
                      </Typography>
                    </Box>
                    <Box>
                      <IconButton size="small" onClick={() => testVoice(v)} title="Play sample">
                        <PlayCircleOutlineIcon color={testAudio === v._id ? 'primary' : 'inherit'} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setEquivalentsEditor({ ...v, equivalents: v.equivalents || {} })}
                        title="Edit cross-provider equivalents"
                      >
                        <SwapHorizIcon
                          color={
                            v.equivalents && Object.values(v.equivalents).some(e => e?.providerVoiceId)
                              ? 'primary'
                              : 'inherit'
                          }
                        />
                      </IconButton>
                      <IconButton size="small" onClick={() => setVoiceEditor({ ...v })}><EditIcon /></IconButton>
                      <IconButton size="small" onClick={() => deleteVoice(v._id)}><DeleteIcon /></IconButton>
                    </Box>
                  </Box>
                  {v.clientDescription && (
                    <Typography variant="body2" sx={{ mt: 1 }}>{v.clientDescription}</Typography>
                  )}
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {v.tags?.gender && <Chip size="small" label={v.tags.gender} />}
                    {v.tags?.accent && <Chip size="small" label={v.tags.accent} />}
                    {v.tags?.ageRange && v.tags.ageRange !== 'unspecified' && <Chip size="small" label={v.tags.ageRange} />}
                    {(v.tags?.style || []).map(s => <Chip key={s} size="small" variant="outlined" label={s} />)}
                    {v.isActive === false && <Chip size="small" color="warning" label="inactive" />}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* -------------------- Avatars tab -------------------- */}
      {tab === 1 && !loading && (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Button startIcon={<AddIcon />} variant="contained" onClick={() => { setAvatarEditor({ ...EMPTY_AVATAR }); setAvatarFile(null); }}>
              Add Avatar
            </Button>
          </Box>
          <Grid container spacing={2}>
            {avatars.map(a => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={a._id}>
                <Paper variant="outlined" sx={{ p: 2, opacity: a.isActive === false ? 0.55 : 1 }}>
                  <Box sx={{ textAlign: 'center', mb: 1 }}>
                    {a.imageUrl ? (
                      <img src={a.imageUrl} alt={a.name} style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: '50%' }} />
                    ) : (
                      <Box sx={{ width: 96, height: 96, mx: 'auto', bgcolor: 'grey.200', borderRadius: '50%' }} />
                    )}
                  </Box>
                  <Typography variant="subtitle2" align="center">{a.name}</Typography>
                  <Typography variant="caption" color="text.secondary" align="center" display="block">{a.style}</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <IconButton size="small" onClick={() => { setAvatarEditor({ ...a }); setAvatarFile(null); }}><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => deleteAvatar(a._id)}><DeleteIcon /></IconButton>
                  </Box>
                  {a.isActive === false && <Chip size="small" color="warning" label="inactive" />}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* -------------------- ElevenLabs library browser -------------------- */}
      <Dialog open={libraryOpen} onClose={() => setLibraryOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>ElevenLabs Voice Library</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Your ElevenLabs workspace voices. Click "Import" to pull one into the catalog — you'll
            be able to set the display name and client-facing description before saving.
          </Typography>
          {libraryError && <Alert severity="error" sx={{ mb: 2 }}>{libraryError}</Alert>}
          {libraryLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!libraryLoading && libraryVoices.length === 0 && !libraryError && (
            <Typography sx={{ p: 2 }} color="text.secondary">No voices returned from ElevenLabs.</Typography>
          )}
          {!libraryLoading && libraryVoices.length > 0 && (
            <List dense>
              {libraryVoices.map(v => {
                const already = isAlreadyInCatalog(v.voice_id);
                const labels = v.labels || {};
                return (
                  <ListItem key={v.voice_id} divider>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span>{v.name}</span>
                          {labels.gender && <Chip size="small" label={labels.gender} />}
                          {labels.accent && <Chip size="small" label={labels.accent} />}
                          {labels.age && <Chip size="small" label={labels.age} />}
                          {already && <Chip size="small" color="success" label="Already in catalog" />}
                        </Box>
                      }
                      secondary={labels.description || labels.use_case || v.voice_id}
                    />
                    <ListItemSecondaryAction>
                      <IconButton onClick={() => previewLibraryVoice(v)} title="Preview">
                        <PlayCircleOutlineIcon color={libraryPreviewing === v.voice_id ? 'primary' : 'inherit'} />
                      </IconButton>
                      <Button
                        size="small"
                        variant={already ? 'outlined' : 'contained'}
                        onClick={() => importFromLibrary(v)}
                        sx={{ ml: 1 }}
                      >
                        {already ? 'Re-import' : 'Import'}
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLibraryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* -------------------- Voice editor dialog -------------------- */}
      {voiceEditor && (
        <Dialog open onClose={() => setVoiceEditor(null)} fullWidth maxWidth="sm">
          <DialogTitle>{voiceEditor._id ? 'Edit Voice' : 'Add Voice'}</DialogTitle>
          <DialogContent>
            <TextField
              label="Display name"
              fullWidth margin="dense"
              value={voiceEditor.name}
              onChange={e => setVoiceEditor({ ...voiceEditor, name: e.target.value })}
              helperText="Shown to therapists in the whitelist and to clients in the voice picker."
            />
            <TextField
              label="Provider Voice ID"
              fullWidth margin="dense"
              value={voiceEditor.providerVoiceId}
              onChange={e => setVoiceEditor({ ...voiceEditor, providerVoiceId: e.target.value })}
              helperText="ElevenLabs voice_id (or other provider's voice id). Auto-filled when imported from the ElevenLabs library."
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Provider</InputLabel>
              <Select
                label="Provider"
                value={voiceEditor.provider}
                onChange={e => setVoiceEditor({ ...voiceEditor, provider: e.target.value })}
              >
                <MenuItem value="elevenlabs">ElevenLabs</MenuItem>
                <MenuItem value="google">Google</MenuItem>
                <MenuItem value="azure">Azure</MenuItem>
                <MenuItem value="openai">OpenAI</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Internal description (admin)"
              fullWidth margin="dense" multiline rows={2}
              value={voiceEditor.internalDescription}
              onChange={e => setVoiceEditor({ ...voiceEditor, internalDescription: e.target.value })}
            />
            <TextField
              label="Client-facing description"
              fullWidth margin="dense" multiline rows={2}
              value={voiceEditor.clientDescription}
              onChange={e => setVoiceEditor({ ...voiceEditor, clientDescription: e.target.value })}
              helperText="Short, plain-language — shown in the client voice picker."
            />
            <TextField
              label="Preview text (optional override)"
              fullWidth margin="dense" multiline rows={2}
              value={voiceEditor.previewText || ''}
              onChange={e => setVoiceEditor({ ...voiceEditor, previewText: e.target.value })}
              helperText="Spoken when previewing this voice. Leave blank to use the platform default. Tokens: {voiceName}, {clientDescription}."
            />
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <FormControl fullWidth margin="dense">
                  <InputLabel>Gender</InputLabel>
                  <Select
                    label="Gender"
                    value={voiceEditor.tags?.gender || 'unspecified'}
                    onChange={e => setVoiceEditor({
                      ...voiceEditor, tags: { ...voiceEditor.tags, gender: e.target.value }
                    })}
                  >
                    {GENDER_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth margin="dense">
                  <InputLabel>Age range</InputLabel>
                  <Select
                    label="Age range"
                    value={voiceEditor.tags?.ageRange || 'unspecified'}
                    onChange={e => setVoiceEditor({
                      ...voiceEditor, tags: { ...voiceEditor.tags, ageRange: e.target.value }
                    })}
                  >
                    {AGE_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Accent"
                  fullWidth margin="dense"
                  value={voiceEditor.tags?.accent || ''}
                  onChange={e => setVoiceEditor({
                    ...voiceEditor, tags: { ...voiceEditor.tags, accent: e.target.value }
                  })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Style tags (comma-sep)"
                  fullWidth margin="dense"
                  value={(voiceEditor.tags?.style || []).join(', ')}
                  onChange={e => setVoiceEditor({
                    ...voiceEditor,
                    tags: {
                      ...voiceEditor.tags,
                      style: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                    }
                  })}
                />
              </Grid>
            </Grid>
            <TextField
              label="Sample URL (optional)"
              fullWidth margin="dense"
              value={voiceEditor.sampleUrl || ''}
              onChange={e => setVoiceEditor({ ...voiceEditor, sampleUrl: e.target.value })}
            />
            <FormControlLabel
              control={<Switch
                checked={voiceEditor.isActive !== false}
                onChange={e => setVoiceEditor({ ...voiceEditor, isActive: e.target.checked })}
              />}
              label="Active"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVoiceEditor(null)}>Cancel</Button>
            <Button variant="contained" onClick={saveVoice}>Save</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* -------------------- Cross-provider equivalents editor -------------------- */}
      {equivalentsEditor && (
        <Dialog open onClose={() => setEquivalentsEditor(null)} fullWidth maxWidth="md">
          <DialogTitle>
            Cross-Provider Equivalents — {equivalentsEditor.name}
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Source voice: <strong>{equivalentsEditor.name}</strong> ({equivalentsEditor.provider}: {equivalentsEditor.providerVoiceId})
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Map this voice to its closest equivalent on each other provider. The unified voice
                resolver uses these equivalents to auto-remap the source voice when synthesis needs
                to happen on a different provider (e.g. cost-saving routing). Leave blank for any
                provider you don't want to map — the resolver falls through to the next hierarchy
                level instead of remapping.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Listen to candidate equivalents</strong> on the target provider's website
                before pasting an ID. See{' '}
                <code>docs/voice-resolver-migration/MAPPING_HOWTO.md</code> for guidance.
              </Typography>
            </Alert>
            {['elevenlabs', 'google', 'azure', 'openai']
              .filter(p => p !== equivalentsEditor.provider) // skip self
              .map(provider => {
                const value = equivalentsEditor.equivalents?.[provider] || {};
                return (
                  <Paper key={provider} variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', mb: 1 }}>
                      {provider}
                    </Typography>
                    <TextField
                      label={`${provider} provider voice ID`}
                      fullWidth margin="dense" size="small"
                      value={value.providerVoiceId || ''}
                      placeholder={
                        provider === 'google' ? 'e.g. en-GB-Neural2-F'
                          : provider === 'azure' ? 'e.g. en-GB-LibbyNeural'
                          : provider === 'openai' ? 'e.g. alloy'
                          : 'e.g. 21m00Tcm4TlvDq8ikWAM'
                      }
                      onChange={e => setEquivalentsEditor({
                        ...equivalentsEditor,
                        equivalents: {
                          ...equivalentsEditor.equivalents,
                          [provider]: { ...value, providerVoiceId: e.target.value }
                        }
                      })}
                    />
                    <TextField
                      label="Quality note (optional)"
                      fullWidth margin="dense" size="small"
                      value={value.qualityNote || ''}
                      placeholder="e.g. Closest GB-female match; not source-identical"
                      onChange={e => setEquivalentsEditor({
                        ...equivalentsEditor,
                        equivalents: {
                          ...equivalentsEditor.equivalents,
                          [provider]: { ...value, qualityNote: e.target.value }
                        }
                      })}
                    />
                  </Paper>
                );
              })}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEquivalentsEditor(null)} disabled={savingEquivalents}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={saveEquivalents}
              disabled={savingEquivalents}
              startIcon={savingEquivalents ? <CircularProgress size={16} /> : null}
            >
              Save equivalents
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* -------------------- Avatar editor dialog -------------------- */}
      {avatarEditor && (
        <Dialog open onClose={() => { setAvatarEditor(null); setAvatarFile(null); }} fullWidth maxWidth="sm">
          <DialogTitle>{avatarEditor._id ? 'Edit Avatar' : 'Add Avatar'}</DialogTitle>
          <DialogContent>
            <TextField
              label="Name"
              fullWidth margin="dense"
              value={avatarEditor.name}
              onChange={e => setAvatarEditor({ ...avatarEditor, name: e.target.value })}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Style</InputLabel>
              <Select
                label="Style"
                value={avatarEditor.style}
                onChange={e => setAvatarEditor({ ...avatarEditor, style: e.target.value })}
              >
                {STYLE_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Internal description"
              fullWidth margin="dense" multiline rows={2}
              value={avatarEditor.description || ''}
              onChange={e => setAvatarEditor({ ...avatarEditor, description: e.target.value })}
            />
            <TextField
              label="Client-facing description (optional)"
              fullWidth margin="dense" multiline rows={2}
              value={avatarEditor.clientDescription || ''}
              onChange={e => setAvatarEditor({ ...avatarEditor, clientDescription: e.target.value })}
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption">Upload image (PNG/JPEG/WebP/SVG, &lt;5MB)</Typography>
              <input
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                onChange={e => setAvatarFile(e.target.files?.[0] || null)}
              />
              {avatarEditor.imageUrl && !avatarFile && (
                <Box sx={{ mt: 1 }}>
                  <img src={avatarEditor.imageUrl} alt="" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }} />
                </Box>
              )}
            </Box>
            <TextField
              label="Or paste image URL"
              fullWidth margin="dense"
              value={avatarEditor.imageUrl || ''}
              onChange={e => setAvatarEditor({ ...avatarEditor, imageUrl: e.target.value })}
              disabled={Boolean(avatarFile)}
            />
            <FormControlLabel
              control={<Switch
                checked={avatarEditor.isActive !== false}
                onChange={e => setAvatarEditor({ ...avatarEditor, isActive: e.target.checked })}
              />}
              label="Active"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setAvatarEditor(null); setAvatarFile(null); }}>Cancel</Button>
            <Button variant="contained" onClick={saveAvatar}>Save</Button>
          </DialogActions>
        </Dialog>
      )}
    </Paper>
  );
};

export default VoiceAndAvatarManager;
